import React from 'react'
import { Button, Select, Space, Segmented } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import type { CalendarView, CalendarDoctor } from './types'
import { getUILabel, formatMonthYear, formatWeekRange, formatDateWithDay } from './utils/locale'
import { startOfWeek } from './utils/time'

interface CalendarHeaderProps {
  currentDate: Date
  view: CalendarView
  locale: 'sr' | 'en'
  doctors?: CalendarDoctor[]
  selectedDoctorId?: string | null
  slotDuration?: number
  showSlotDurationPicker?: boolean
  onPrevious: () => void
  onNext: () => void
  onToday: () => void
  onViewChange: (view: CalendarView) => void
  onDoctorChange?: (doctorId: string | null) => void
  onSlotDurationChange?: (duration: number) => void
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  view,
  locale,
  doctors,
  selectedDoctorId,
  slotDuration = 30,
  showSlotDurationPicker = false,
  onPrevious,
  onNext,
  onToday,
  onViewChange,
  onDoctorChange,
  onSlotDurationChange,
}) => {
  const weekStart = startOfWeek(currentDate)

  const getTitle = () => {
    switch (view) {
      case 'day':
        return formatDateWithDay(currentDate, locale)
      case 'week':
        return formatWeekRange(weekStart, locale)
      case 'month':
        return formatMonthYear(currentDate, locale)
    }
  }

  const viewOptions = [
    { label: getUILabel('day', locale), value: 'day' },
    { label: getUILabel('week', locale), value: 'week' },
    { label: getUILabel('month', locale), value: 'month' },
  ]

  const slotDurationOptions = [
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '60 min', value: 60 },
  ]

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      {/* Navigation */}
      <Space>
        <Button.Group>
          <Button icon={<LeftOutlined />} onClick={onPrevious} />
          <Button onClick={onToday}>{getUILabel('today', locale)}</Button>
          <Button icon={<RightOutlined />} onClick={onNext} />
        </Button.Group>
      </Space>

      {/* Title */}
      <h2
        style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 600,
          color: '#111827',
          textTransform: 'capitalize',
          flex: 1,
          textAlign: 'center',
          minWidth: '200px',
        }}
      >
        {getTitle()}
      </h2>

      {/* View Switcher, Slot Duration, and Doctor Filter */}
      <Space>
        {/* Slot Duration Picker (only for day/week views) */}
        {showSlotDurationPicker &&
          onSlotDurationChange &&
          (view === 'day' || view === 'week') && (
            <Select
              value={slotDuration}
              onChange={onSlotDurationChange}
              style={{ minWidth: 90 }}
              options={slotDurationOptions}
              size="small"
            />
          )}

        {/* Doctor Filter */}
        {doctors && doctors.length > 0 && onDoctorChange && (
          <Select
            value={selectedDoctorId || 'all'}
            onChange={(value) => onDoctorChange(value === 'all' ? null : value)}
            style={{ minWidth: 160 }}
            options={[
              { label: getUILabel('allDoctors', locale), value: 'all' },
              ...doctors.map((doc) => ({
                label: `${doc.title || ''} ${doc.name}`.trim(),
                value: doc.id,
              })),
            ]}
          />
        )}

        {/* View Switcher */}
        <Segmented
          value={view}
          onChange={(value) => onViewChange(value as CalendarView)}
          options={viewOptions}
        />
      </Space>
    </div>
  )
}
